<script setup>
import { computed, h, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import { DropdownSection, DropdownItem } from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

const showAvailabilitySheet = ref(false);

const openAvailabilitySheet = () => {
  showAvailabilitySheet.value = true;
};

const closeAvailabilitySheet = () => {
  showAvailabilitySheet.value = false;
};

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
    closeAvailabilitySheet();
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection>
    <div class="grid gap-0">
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <Button
          size="sm"
          color="slate"
          variant="faded"
          class="min-w-[120px]"
          icon="i-lucide-chevron-down"
          trailing-icon
          @click="openAvailabilitySheet"
        >
          <div class="flex gap-1 items-center flex-grow text-sm">
            <div class="p-1 flex-shrink-0">
              <div class="size-2 rounded-sm" :class="activeStatus.color" />
            </div>
            <span>{{ activeStatus.label }}</span>
          </div>
        </Button>
        <woot-modal
          v-model:show="showAvailabilitySheet"
          :on-close="closeAvailabilitySheet"
        >
          <div class="grid gap-4 p-4">
            <p class="text-base font-semibold text-n-slate-12">
              {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
            </p>
            <div class="grid gap-3">
              <button
                v-for="status in availabilityStatuses"
                :key="status.value"
                type="button"
                class="flex items-center justify-between rounded-xl border px-4 py-3 text-left text-base font-medium transition-colors"
                :class="
                  status.active
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-n-weak text-n-slate-12'
                "
                @click="changeAvailabilityStatus(status.value)"
              >
                <span>{{ status.label }}</span>
                <span class="size-2 rounded-sm" :class="status.color" />
              </button>
            </div>
          </div>
        </woot-modal>
      </DropdownItem>
      <DropdownItem>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
        </div>
        <div class="flex-shrink-0">
          <ToggleSwitch
            v-model="autoOfflineToggle"
            class="origin-right scale-110"
          />
        </div>
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
